import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, 
  ChevronRight, 
  ChevronDown,
  ExternalLink, 
  Copy, 
  Check, 
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Settings,
  Code,
  User,
  Loader2,
  Zap,
  Play,
  CheckCheck
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api';

/**
 * Social Setup Expert Drawer v1.0
 * ================================
 * P0: Intelligent copilot for social media platform configuration.
 * 
 * Features:
 * - Block 1: What's missing
 * - Block 2: Steps to follow
 * - Block 3: Next recommended action
 * - Block 4: Common problems
 * - Block 5: Technical details (Developer mode)
 * 
 * Modes:
 * - Novice: Simple language, guided steps, no jargon
 * - Developer: OAuth scopes, callback URLs, API logs, raw errors
 */

// Platform display names and colors
const PLATFORM_INFO = {
  facebook: { name: 'Facebook', color: '#1877F2', icon: 'fb' },
  instagram: { name: 'Instagram', color: '#E1306C', icon: 'ig' },
  x_twitter: { name: 'X (Twitter)', color: '#000000', icon: 'x' },
  pinterest: { name: 'Pinterest', color: '#E60023', icon: 'pin' },
  telegram: { name: 'Telegram', color: '#0088cc', icon: 'tg' },
  brevo: { name: 'Email (Brevo)', color: '#0B996E', icon: 'mail' },
  linkedin: { name: 'LinkedIn', color: '#0A66C2', icon: 'li' }
};

// Connection state display
const CONNECTION_STATES = {
  not_started: { label: 'Non configure', color: 'var(--color-text-muted)', icon: HelpCircle },
  app_config_missing: { label: 'App ID/Secret manquant', color: 'var(--color-warning)', icon: AlertCircle },
  oauth_required: { label: 'Connexion OAuth requise', color: 'var(--color-warning)', icon: AlertCircle },
  oauth_in_progress: { label: 'OAuth en cours...', color: 'var(--color-primary)', icon: Loader2 },
  page_selection_required: { label: 'Selection de Page requise', color: 'var(--color-warning)', icon: AlertCircle },
  token_missing: { label: 'Token manquant', color: 'var(--color-error)', icon: AlertCircle },
  token_expired: { label: 'Token expire', color: 'var(--color-error)', icon: AlertCircle },
  permission_denied: { label: 'Permission refusee', color: 'var(--color-error)', icon: AlertCircle },
  connected: { label: 'Connecte', color: 'var(--color-success)', icon: CheckCircle2 },
  error: { label: 'Erreur', color: 'var(--color-error)', icon: AlertCircle }
};

export default function SocialSetupExpertDrawer({ 
  isOpen, 
  onClose, 
  platform,
  projectId,
  onAction 
}) {
  const [mode, setMode] = useState('novice'); // 'novice' or 'developer'
  const [loading, setLoading] = useState(false);
  const [guidance, setGuidance] = useState(null);
  const [context, setContext] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    missing: true,
    steps: true,
    problems: false,
    technical: false
  });
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Fallback guidance when API fails or user not authenticated
  const getFallbackGuidance = useCallback((platformId) => {
    if (platformId === 'facebook' || platformId === 'instagram') {
      return {
        what_is_missing: [
          "Configuration de l'application Meta non completee",
          "App ID Meta manquant ou non verifie",
          "App Secret Meta manquant"
        ],
        why_it_blocks: "Pour connecter Facebook/Instagram, vous devez d'abord creer une application sur le portail Meta Developer et recuperer les identifiants (App ID et App Secret).",
        steps: [
          {
            order: 1,
            title: "Acceder au portail Meta Developer",
            description: "Ouvrez developers.facebook.com et connectez-vous avec votre compte Facebook.",
            tip: "Utilisez le meme compte qui gere votre Page Facebook.",
            url: "https://developers.facebook.com/apps"
          },
          {
            order: 2,
            title: "Creer une nouvelle application",
            description: "Cliquez sur 'Creer une application' (bouton vert en haut a droite).",
            tip: "Si vous avez deja une app, vous pouvez la reutiliser."
          },
          {
            order: 3,
            title: "Choisir le type d'application",
            description: "Selectionnez 'Entreprise' ou 'Aucun' selon votre cas.",
            tip: "Pour la plupart des utilisateurs, 'Aucun' convient parfaitement."
          },
          {
            order: 4,
            title: "Nommer votre application",
            description: "Donnez un nom (ex: 'Mon Marketing App') et entrez votre email.",
            tip: "Ce nom n'est visible que par vous dans le portail Meta."
          },
          {
            order: 5,
            title: "Recuperer l'App ID",
            description: "Une fois creee, l'App ID s'affiche en haut du tableau de bord.",
            tip: "C'est un nombre comme '1234567890123456'. Copiez-le."
          },
          {
            order: 6,
            title: "Recuperer l'App Secret",
            description: "Allez dans Parametres > Base. Cliquez 'Afficher' a cote de 'Cle secrete'.",
            tip: "IMPORTANT: Ne partagez JAMAIS cette cle. Elle donne acces a votre app."
          }
        ],
        next_action: {
          label: "Ouvrir Meta Developer Portal",
          action_type: "external_link",
          payload: "https://developers.facebook.com/apps"
        },
        common_problems: [
          {
            symptom: "Erreur 'compte non professionnel'",
            solution: "Convertissez votre compte Instagram en Business dans Parametres > Compte > Passer en compte pro."
          },
          {
            symptom: "Aucune Page Facebook trouvee",
            solution: "Creez une Page Facebook ou verifiez que vous etes administrateur d'une Page existante."
          },
          {
            symptom: "Instagram non visible apres connexion",
            solution: "Verifiez que votre Instagram Business est lie a la Page Facebook selectionnee."
          },
          {
            symptom: "Token expire (erreur 190)",
            solution: "Les tokens Meta expirent apres 60 jours. Reconnectez votre compte via OAuth."
          },
          {
            symptom: "Permissions insuffisantes (erreur 10/200)",
            solution: "Lors de la connexion OAuth, acceptez TOUTES les permissions demandees."
          },
          {
            symptom: "Erreur 'redirect_uri_mismatch'",
            solution: "Copiez la Callback URL exacte dans Facebook Login > Parametres > URI de redirection OAuth."
          }
        ],
        technical_details: {
          required_scopes: platformId === 'instagram' 
            ? ['instagram_basic', 'instagram_content_publish', 'pages_show_list']
            : ['pages_manage_posts', 'pages_read_engagement', 'pages_show_list'],
          oauth_type: 'OAuth 2.0',
          token_expiry: '60 days (long-lived)',
          api_version: 'v18.0'
        }
      };
    } else if (platformId === 'x_twitter') {
      return {
        what_is_missing: ["Configuration X (Twitter) non completee"],
        why_it_blocks: "Pour connecter X, vous devez avoir un compte Developer X avec une application configuree.",
        steps: [
          {
            order: 1,
            title: "Acceder au Developer Portal X",
            description: "Ouvrez developer.twitter.com et connectez-vous.",
            tip: "Vous devez avoir un compte X verifie (email + telephone).",
            url: "https://developer.twitter.com/en/portal/dashboard"
          },
          {
            order: 2,
            title: "Creer un projet",
            description: "Cliquez 'Create Project' et donnez-lui un nom.",
            tip: "Choisissez 'Production' si vous voulez publier vraiment."
          }
        ],
        next_action: {
          label: "Ouvrir X Developer Portal",
          action_type: "external_link",
          payload: "https://developer.twitter.com/en/portal/dashboard"
        },
        common_problems: [
          {
            symptom: "Erreur 'Unauthorized' (401)",
            solution: "Regenerez vos tokens dans le Developer Portal X et reconnectez."
          },
          {
            symptom: "Rate limit atteint (429)",
            solution: "L'API Free X est limitee a 1500 tweets/mois. Attendez ou passez au plan Basic."
          },
          {
            symptom: "Erreur OAuth 'callback mismatch'",
            solution: "Verifiez que la Callback URL est exactement configuree dans votre app X."
          },
          {
            symptom: "Image non publiee",
            solution: "Bug connu de l'API X Free. Le texte est publie mais l'image peut echouer."
          }
        ],
        technical_details: {
          required_scopes: ['tweet.read', 'tweet.write', 'users.read', 'offline.access'],
          oauth_type: 'OAuth 2.0 PKCE',
          rate_limit: '1500 tweets/month (Free tier)',
          api_version: 'v2'
        }
      };
    } else if (platformId === 'pinterest') {
      return {
        what_is_missing: ["Configuration Pinterest non completee"],
        why_it_blocks: "Pour connecter Pinterest, vous devez creer une application sur le Pinterest Developer Portal.",
        steps: [
          {
            order: 1,
            title: "Acceder au Developer Portal Pinterest",
            description: "Ouvrez developers.pinterest.com et connectez-vous.",
            tip: "Utilisez votre compte Pinterest personnel ou business.",
            url: "https://developers.pinterest.com/apps/"
          },
          {
            order: 2,
            title: "Creer une nouvelle application",
            description: "Cliquez sur 'Create app' et remplissez les informations.",
            tip: "Le nom de l'app doit etre unique."
          },
          {
            order: 3,
            title: "Configurer les permissions",
            description: "Activez les scopes 'boards:read', 'pins:read', 'pins:write'.",
            tip: "Ces permissions sont necessaires pour creer des pins."
          },
          {
            order: 4,
            title: "Recuperer App ID et Secret",
            description: "Une fois l'app creee, copiez l'App ID et le Secret.",
            tip: "Le Secret n'est affiche qu'une fois."
          }
        ],
        next_action: {
          label: "Ouvrir Pinterest Developer Portal",
          action_type: "external_link",
          payload: "https://developers.pinterest.com/apps/"
        },
        common_problems: [
          {
            symptom: "Erreur 'Trial access'",
            solution: "Votre app est en mode Trial. Demandez l'acces Production dans le Developer Portal."
          },
          {
            symptom: "Board non trouve",
            solution: "Selectionnez un board existant dans la configuration. Creez-en un si necessaire."
          },
          {
            symptom: "Pin refuse",
            solution: "Verifiez que l'image respecte les dimensions Pinterest (min 100x100px)."
          }
        ],
        technical_details: {
          required_scopes: ['boards:read', 'pins:read', 'pins:write'],
          oauth_type: 'OAuth 2.0',
          api_version: 'v5'
        }
      };
    } else if (platformId === 'telegram') {
      return {
        what_is_missing: ["Configuration Telegram non completee", "Bot Token manquant"],
        why_it_blocks: "Pour connecter Telegram, vous devez creer un bot via BotFather et recuperer son token.",
        steps: [
          {
            order: 1,
            title: "Ouvrir BotFather sur Telegram",
            description: "Recherchez @BotFather dans Telegram et ouvrez la conversation.",
            tip: "BotFather est le bot officiel de Telegram pour creer des bots.",
            url: "https://t.me/botfather"
          },
          {
            order: 2,
            title: "Creer un nouveau bot",
            description: "Envoyez la commande /newbot et suivez les instructions.",
            tip: "Choisissez un nom qui finit par 'bot' (ex: MonMarketingBot)."
          },
          {
            order: 3,
            title: "Recuperer le token",
            description: "BotFather vous donnera un token. Copiez-le entierement.",
            tip: "Le token ressemble a: 123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
          },
          {
            order: 4,
            title: "Configurer le Chat ID",
            description: "Ajoutez le bot a votre groupe/canal et recuperez le Chat ID.",
            tip: "Utilisez @userinfobot pour trouver votre Chat ID."
          }
        ],
        next_action: {
          label: "Ouvrir BotFather",
          action_type: "external_link",
          payload: "https://t.me/botfather"
        },
        common_problems: [
          {
            symptom: "Message non envoye",
            solution: "Verifiez que le bot est administrateur du groupe/canal."
          },
          {
            symptom: "Chat ID invalide",
            solution: "Les IDs de groupe commencent par -100. Verifiez le format."
          },
          {
            symptom: "Token invalide",
            solution: "Regenerez un nouveau token via /token chez BotFather."
          }
        ],
        technical_details: {
          api_type: 'Telegram Bot API',
          auth_method: 'Bot Token',
          rate_limit: '30 messages/second'
        }
      };
    }
    // Generic fallback for other platforms
    return {
      what_is_missing: ["Configuration en cours"],
      why_it_blocks: "Cette plateforme necessite une configuration specifique.",
      steps: [{ order: 1, title: "Ouvrir la configuration", description: "Cliquez sur 'Configurer' pour cette plateforme.", tip: "Suivez les instructions a l'ecran." }],
      next_action: { label: "Configurer", action_type: "open_modal", payload: platformId },
      common_problems: [],
      technical_details: null
    };
  }, []);

  const getFallbackContext = useCallback((platformId) => {
    // Use the API base URL from environment or construct from current location
    const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return {
      connection_state: 'not_started',
      has_app_id: false,
      has_app_secret: false,
      has_user_token: false,
      has_page_token: false,
      has_page_selected: false,
      expected_callback_url: platformId === 'facebook' || platformId === 'instagram' 
        ? `${apiBaseUrl}/api/auth/facebook/callback`
        : platformId === 'x_twitter'
        ? `${apiBaseUrl}/api/auth/x/callback`
        : `${apiBaseUrl}/api/auth/${platformId}/callback`
    };
  }, []);

  // P0 SOCIAL SETUP EXPERT: Track active platform (can be from prop or local selection)
  // '__selector__' is a special value meaning "show the platform selector"
  const [selectedPlatformLocal, setSelectedPlatformLocal] = useState(
    platform === '__selector__' ? null : platform
  );
  
  // Update local selection when prop changes (ignore special selector value)
  useEffect(() => {
    if (platform && platform !== '__selector__') {
      setSelectedPlatformLocal(platform);
    } else if (platform === '__selector__') {
      // Reset to show selector
      setSelectedPlatformLocal(null);
    }
  }, [platform]);
  
  // activePlatform is null when we want to show the selector
  const activePlatform = selectedPlatformLocal || (platform !== '__selector__' ? platform : null);

  // Fetch guidance when platform changes
  const fetchGuidance = useCallback(async () => {
    // Don't fetch if no real platform selected
    if (!activePlatform || !isOpen || activePlatform === '__selector__') return;
    
    console.info(`[SocialSetupExpert] Opening drawer for platform=${activePlatform} mode=${mode}`);
    setLoading(true);
    try {
      const response = await apiClient.get(`/social-setup-expert/${activePlatform}`, {
        params: { mode, project_id: projectId }
      });
      
      console.info(`[SocialSetupExpert] API response received:`, response.data?.context?.connection_state);
      setGuidance(response.data.guidance);
      setContext(response.data.context);
    } catch (error) {
      console.warn('[SocialSetupExpert] API error, using fallback guidance:', error.response?.status || error.message);
      // P0 FIX: Use fallback data instead of showing error
      setGuidance(getFallbackGuidance(activePlatform));
      setContext(getFallbackContext(activePlatform));
    } finally {
      setLoading(false);
    }
  }, [activePlatform, mode, projectId, isOpen, getFallbackGuidance, getFallbackContext]);

  useEffect(() => {
    fetchGuidance();
  }, [fetchGuidance]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // State for connection test
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null); // 'success' | 'error' | null

  // Test connection function
  const handleTestConnection = async () => {
    if (!activePlatform) return;
    
    setTestingConnection(true);
    setTestResult(null);
    
    console.info(`[SocialSetupExpert] Testing connection for ${activePlatform}`);
    
    try {
      let endpoint = '';
      
      // Determine the correct test endpoint for each platform
      if (activePlatform === 'facebook') {
        endpoint = '/social/facebook/test';
      } else if (activePlatform === 'instagram') {
        endpoint = '/social/instagram/test';
      } else if (activePlatform === 'x_twitter') {
        endpoint = '/social/x/test';
      } else if (activePlatform === 'pinterest') {
        endpoint = '/social/pinterest/test';
      } else if (activePlatform === 'telegram') {
        endpoint = '/integrations/telegram/test';
      } else if (activePlatform === 'brevo') {
        endpoint = '/integrations/brevo/test';
      } else {
        endpoint = `/integrations/${activePlatform}/status`;
      }
      
      const response = await apiClient.post(endpoint, { project_id: projectId });
      
      if (response.data?.success) {
        setTestResult('success');
        toast.success('✅ Connexion validée !');
        // Refresh the guidance to show updated state
        setTimeout(() => fetchGuidance(), 1500);
      } else {
        setTestResult('error');
        const errorMsg = response.data?.error || 'Test échoué';
        
        // Handle specific errors
        if (errorMsg.includes('Trial access')) {
          toast.error(
            <div>
              <strong>⚠️ Limitation Trial</strong>
              <div className="text-sm mt-1">App en mode Trial - publication production non autorisée</div>
            </div>,
            { duration: 6000 }
          );
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      setTestResult('error');
      const errorStatus = error.response?.status;
      const errorDetail = error.response?.data?.detail || error.response?.data?.error || error.message;
      
      console.error(`[SocialSetupExpert] Test failed:`, errorStatus, errorDetail);
      
      // Handle 401 - not authenticated
      if (errorStatus === 401) {
        toast.error('Session expirée - Reconnectez-vous d\'abord');
      } else if (errorStatus === 405) {
        toast.error('Erreur technique (405) - La connexion semble active');
      } else if (errorDetail?.includes?.('Trial access')) {
        toast.error(
          <div>
            <strong>⚠️ Limitation Trial</strong>
            <div className="text-sm mt-1">App en mode Trial</div>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(errorDetail || 'Erreur lors du test');
      }
    } finally {
      setTestingConnection(false);
    }
  };

  // Copy callback URL
  const handleCopyCallback = async () => {
    if (context?.expected_callback_url) {
      try {
        await navigator.clipboard.writeText(context.expected_callback_url);
        setCopiedUrl(true);
        toast.success('URL copiee !');
        setTimeout(() => setCopiedUrl(false), 2000);
      } catch (err) {
        toast.error('Impossible de copier');
      }
    }
  };

  // Handle action button clicks
  const handleAction = (action) => {
    if (!action) return;
    
    switch (action.action_type) {
      case 'external_link':
        window.open(action.payload, '_blank');
        break;
      case 'oauth':
        onAction?.({ type: 'oauth', platform: action.payload });
        break;
      case 'select_page':
        onAction?.({ type: 'select_page', platform: action.payload });
        break;
      case 'open_modal':
        onAction?.({ type: 'open_modal', platform: action.payload });
        break;
      case 'navigate':
        onAction?.({ type: 'navigate', path: action.payload });
        break;
      case 'focus_field':
        onAction?.({ type: 'focus_field', field: action.payload });
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  if (!isOpen) return null;

  // P0 SOCIAL SETUP EXPERT: Derived values for rendering
  const platformInfo = PLATFORM_INFO[activePlatform] || { name: 'Réseaux sociaux', color: '#6366f1' };
  const connectionState = CONNECTION_STATES[context?.connection_state] || CONNECTION_STATES.not_started;
  const StateIcon = connectionState.icon;

  // Platform selector component
  const PlatformSelector = () => (
    <div className="p-6 space-y-6">
      <div className="text-center space-y-2">
        <div 
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto"
          style={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
          }}
        >
          <HelpCircle className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-semantic">
          Social Setup Expert
        </h3>
        <p className="text-sm text-semantic-muted">
          Sélectionnez une plateforme pour obtenir de l'aide contextuelle sur sa configuration.
        </p>
      </div>
      
      <div className="space-y-2">
        {Object.entries(PLATFORM_INFO).map(([id, info]) => (
          <button
            key={id}
            onClick={() => {
              setSelectedPlatformLocal(id);
              onAction?.({ type: 'switch_platform', platform: id });
            }}
            className="w-full flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02]"
            style={{
              backgroundColor: 'hsl(var(--color-surface-2))',
              border: '1px solid hsl(var(--color-border-subtle))'
            }}
            data-testid={`select-platform-${id}`}
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: info.color, color: 'white' }}
            >
              <Zap className="w-5 h-5" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-medium text-semantic">{info.name}</div>
              <div className="text-xs text-semantic-muted">
                Guide de configuration complet
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-semantic-muted" />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
        data-testid="expert-drawer-backdrop"
      />
      
      {/* Drawer */}
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-md z-50 overflow-hidden animate-in slide-in-from-right duration-300"
        style={{
          backgroundColor: 'hsl(var(--color-surface))',
          borderLeft: '1px solid hsl(var(--color-border))'
        }}
        data-testid="social-setup-expert-drawer"
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: 'hsl(var(--color-border))' }}
        >
          <div className="flex items-center gap-3">
            {/* P0: Back button when platform is selected (to return to selector) */}
            {activePlatform && !platform && (
              <button
                onClick={() => setSelectedPlatformLocal(null)}
                className="p-2 -ml-2 rounded-lg hover:bg-semantic-surface-2 text-semantic-muted hover:text-semantic transition-colors"
                title="Retour au sélecteur"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
              </button>
            )}
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: platformInfo.color, color: 'white' }}
            >
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-semantic">
                {activePlatform ? `Expert ${platformInfo.name}` : 'Social Setup Expert'}
              </h2>
              {activePlatform && (
                <div className="flex items-center gap-1.5 text-xs">
                  <StateIcon 
                    className={cn("w-3.5 h-3.5", connectionState.icon === Loader2 && "animate-spin")}
                    style={{ color: `hsl(${connectionState.color})` }}
                  />
                  <span style={{ color: `hsl(${connectionState.color})` }}>
                    {connectionState.label}
                  </span>
                </div>
              )}
              {!activePlatform && (
                <div className="text-xs text-semantic-muted">
                  Choisissez une plateforme
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Mode Toggle - Only show when platform is selected */}
            {activePlatform && (
              <div 
                className="flex rounded-lg p-0.5 text-xs"
                style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              >
                <button
                  onClick={() => setMode('novice')}
                  className={cn(
                    "px-2.5 py-1 rounded-md transition-all flex items-center gap-1",
                    mode === 'novice' 
                      ? "bg-white dark:bg-gray-700 shadow-sm text-semantic font-medium" 
                      : "text-semantic-muted hover:text-semantic"
                  )}
                  data-testid="mode-novice-btn"
                >
                  <User className="w-3 h-3" />
                  Simple
                </button>
                <button
                  onClick={() => setMode('developer')}
                  className={cn(
                    "px-2.5 py-1 rounded-md transition-all flex items-center gap-1",
                    mode === 'developer' 
                      ? "bg-white dark:bg-gray-700 shadow-sm text-semantic font-medium" 
                      : "text-semantic-muted hover:text-semantic"
                  )}
                  data-testid="mode-developer-btn"
                >
                  <Code className="w-3 h-3" />
                  Dev
                </button>
              </div>
            )}
            
            {/* Refresh - Only show when platform is selected */}
            {activePlatform && (
              <button
                onClick={fetchGuidance}
                disabled={loading}
                className="p-2 rounded-lg hover:bg-semantic-surface-2 text-semantic-muted hover:text-semantic transition-colors"
                title="Actualiser"
              >
                <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
              </button>
            )}
            
            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-semantic-surface-2 text-semantic-muted hover:text-semantic transition-colors"
              data-testid="close-expert-drawer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-160px)] p-4 space-y-4">
          {/* P0: Show platform selector when no platform is active */}
          {!activePlatform ? (
            <PlatformSelector />
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-semantic-muted" />
            </div>
          ) : guidance ? (
            <>
              {/* BLOCK 1: What's Missing */}
              {guidance.what_is_missing?.length > 0 && (
                <CollapsibleBlock
                  title="Ce qui manque actuellement"
                  icon={<AlertCircle className="w-4 h-4" />}
                  iconColor="var(--color-warning)"
                  expanded={expandedSections.missing}
                  onToggle={() => toggleSection('missing')}
                  testId="block-missing"
                >
                  <ul className="space-y-2">
                    {guidance.what_is_missing.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-semantic">
                        <span 
                          className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                          style={{ backgroundColor: 'hsl(var(--color-warning))' }}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                  
                  {guidance.why_it_blocks && (
                    <p className="mt-3 text-xs text-semantic-muted p-3 rounded-lg bg-semantic-surface-2">
                      <strong>Pourquoi ca bloque :</strong> {guidance.why_it_blocks}
                    </p>
                  )}
                </CollapsibleBlock>
              )}
              
              {/* BLOCK 2: Steps to Follow */}
              {guidance.steps?.length > 0 && (
                <CollapsibleBlock
                  title="Etapes a suivre"
                  icon={<Settings className="w-4 h-4" />}
                  iconColor="var(--color-primary)"
                  expanded={expandedSections.steps}
                  onToggle={() => toggleSection('steps')}
                  testId="block-steps"
                >
                  <div className="space-y-3">
                    {guidance.steps.map((step, i) => (
                      <div 
                        key={i} 
                        className="flex gap-3 p-3 rounded-xl"
                        style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
                      >
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                          style={{ 
                            backgroundColor: 'hsl(var(--color-primary))',
                            color: 'white'
                          }}
                        >
                          {step.order || i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-semantic mb-1">
                            {step.title}
                          </h4>
                          <p className="text-xs text-semantic-muted mb-2">
                            {step.description}
                          </p>
                          {step.tip && (
                            <p className="text-xs text-semantic-muted italic">
                              💡 {step.tip}
                            </p>
                          )}
                          {step.url && (
                            <button
                              onClick={() => window.open(step.url, '_blank')}
                              className="mt-2 text-xs flex items-center gap-1 text-primary hover:underline"
                            >
                              Ouvrir <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleBlock>
              )}
              
              {/* Callback URL (Always visible in Dev mode, or when not connected) */}
              {context?.expected_callback_url && (mode === 'developer' || context.connection_state !== 'connected') && (
                <div 
                  className="p-3 rounded-xl border"
                  style={{ 
                    backgroundColor: 'hsl(var(--color-surface-2))',
                    borderColor: 'hsl(var(--color-border))'
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-semantic">Callback URL OAuth</span>
                    <button
                      onClick={handleCopyCallback}
                      className="flex items-center gap-1 text-xs text-primary hover:underline"
                      data-testid="copy-callback-btn"
                    >
                      {copiedUrl ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copiedUrl ? 'Copie !' : 'Copier'}
                    </button>
                  </div>
                  <code 
                    className="text-xs block p-2 rounded bg-semantic-surface-3 text-semantic break-all font-mono"
                  >
                    {context.expected_callback_url}
                  </code>
                  <p className="text-[10px] text-semantic-muted mt-2">
                    Copiez cette URL exacte dans les parametres OAuth de votre application {platform === 'facebook' || platform === 'instagram' ? 'Meta' : platform}.
                  </p>
                </div>
              )}
              
              {/* BLOCK 4: Common Problems */}
              {guidance.common_problems?.length > 0 && (
                <CollapsibleBlock
                  title="Problemes frequents"
                  icon={<HelpCircle className="w-4 h-4" />}
                  iconColor="var(--color-text-secondary)"
                  expanded={expandedSections.problems}
                  onToggle={() => toggleSection('problems')}
                  testId="block-problems"
                >
                  <div className="space-y-2">
                    {guidance.common_problems.map((problem, i) => (
                      <div 
                        key={i} 
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
                      >
                        <p className="text-xs font-medium text-semantic mb-1">
                          {problem.symptom}
                        </p>
                        <p className="text-xs text-semantic-muted">
                          → {problem.solution}
                        </p>
                      </div>
                    ))}
                  </div>
                </CollapsibleBlock>
              )}
              
              {/* BLOCK 5: Technical Details (Developer mode only) */}
              {mode === 'developer' && (
                <CollapsibleBlock
                  title="Details techniques"
                  icon={<Code className="w-4 h-4" />}
                  iconColor="var(--color-text-secondary)"
                  expanded={expandedSections.technical}
                  onToggle={() => toggleSection('technical')}
                  testId="block-technical"
                >
                  {/* Connection Status Grid */}
                  {context && (
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div className="p-2 rounded-lg bg-semantic-surface-3">
                        <span className="text-semantic-muted">App ID</span>
                        <div className={context.has_app_id ? "text-green-600 font-medium" : "text-red-500"}>
                          {context.has_app_id ? '✓ Configuré' : '✗ Manquant'}
                        </div>
                      </div>
                      <div className="p-2 rounded-lg bg-semantic-surface-3">
                        <span className="text-semantic-muted">App Secret</span>
                        <div className={context.has_app_secret ? "text-green-600 font-medium" : "text-red-500"}>
                          {context.has_app_secret ? '✓ Configuré' : '✗ Manquant'}
                        </div>
                      </div>
                      <div className="p-2 rounded-lg bg-semantic-surface-3">
                        <span className="text-semantic-muted">User Token</span>
                        <div className={context.has_user_token ? "text-green-600 font-medium" : "text-red-500"}>
                          {context.has_user_token ? '✓ Actif' : '✗ Non obtenu'}
                        </div>
                      </div>
                      <div className="p-2 rounded-lg bg-semantic-surface-3">
                        <span className="text-semantic-muted">Page Token</span>
                        <div className={context.has_page_token ? "text-green-600 font-medium" : "text-red-500"}>
                          {context.has_page_token ? '✓ Actif' : '✗ Non obtenu'}
                        </div>
                      </div>
                      {context.page_name && (
                        <div className="p-2 rounded-lg bg-semantic-surface-3 col-span-2">
                          <span className="text-semantic-muted">Page/Board</span>
                          <div className="text-semantic font-medium">{context.page_name}</div>
                        </div>
                      )}
                      <div className="p-2 rounded-lg bg-semantic-surface-3 col-span-2">
                        <span className="text-semantic-muted">État de connexion</span>
                        <div className="text-semantic font-mono text-[11px]">{context.connection_state}</div>
                      </div>
                    </div>
                  )}
                  
                  {/* API Details */}
                  {guidance.technical_details && (
                    <div className="space-y-2 text-xs">
                      <div className="font-medium text-semantic mb-2">Paramètres API</div>
                      {guidance.technical_details.required_scopes && (
                        <div className="p-2 rounded-lg bg-semantic-surface-3">
                          <span className="text-semantic-muted block mb-1">Scopes OAuth requis</span>
                          <code className="text-[10px] text-semantic font-mono">
                            {guidance.technical_details.required_scopes.join(', ')}
                          </code>
                        </div>
                      )}
                      {guidance.technical_details.oauth_type && (
                        <div className="flex justify-between p-2 rounded-lg bg-semantic-surface-3">
                          <span className="text-semantic-muted">Type OAuth</span>
                          <span className="text-semantic font-mono">{guidance.technical_details.oauth_type}</span>
                        </div>
                      )}
                      {guidance.technical_details.api_version && (
                        <div className="flex justify-between p-2 rounded-lg bg-semantic-surface-3">
                          <span className="text-semantic-muted">Version API</span>
                          <span className="text-semantic font-mono">{guidance.technical_details.api_version}</span>
                        </div>
                      )}
                      {guidance.technical_details.rate_limit && (
                        <div className="flex justify-between p-2 rounded-lg bg-semantic-surface-3">
                          <span className="text-semantic-muted">Rate Limit</span>
                          <span className="text-semantic font-mono text-[10px]">{guidance.technical_details.rate_limit}</span>
                        </div>
                      )}
                      {guidance.technical_details.token_expiry && (
                        <div className="flex justify-between p-2 rounded-lg bg-semantic-surface-3">
                          <span className="text-semantic-muted">Expiration token</span>
                          <span className="text-semantic font-mono">{guidance.technical_details.token_expiry}</span>
                        </div>
                      )}
                    </div>
                  )}
                </CollapsibleBlock>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-semantic-muted">
              Aucune donnee disponible
            </div>
          )}
        </div>
        
        {/* Footer - Actions */}
        <div 
          className="absolute bottom-0 left-0 right-0 p-4 border-t"
          style={{ 
            borderColor: 'hsl(var(--color-border))',
            backgroundColor: 'hsl(var(--color-surface))'
          }}
        >
          {/* Test Connection Button - Only show when there's some config */}
          {context && (context.has_app_id || context.has_user_token) && (
            <button
              onClick={handleTestConnection}
              disabled={testingConnection}
              className={cn(
                "w-full py-2.5 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 mb-2",
                testResult === 'success' 
                  ? "bg-green-500/10 text-green-600 border border-green-500/30"
                  : testResult === 'error'
                  ? "bg-red-500/10 text-red-600 border border-red-500/30"
                  : "bg-semantic-surface-2 text-semantic border border-semantic-border hover:bg-semantic-surface-3"
              )}
              data-testid="test-connection-btn"
            >
              {testingConnection ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Test en cours...
                </>
              ) : testResult === 'success' ? (
                <>
                  <CheckCheck className="w-4 h-4" />
                  Connexion validée
                </>
              ) : testResult === 'error' ? (
                <>
                  <AlertCircle className="w-4 h-4" />
                  Test échoué - Réessayer
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Tester la connexion
                </>
              )}
            </button>
          )}
          
          {/* Main Action Button */}
          {guidance?.next_action && (
            <>
              <button
                onClick={() => handleAction(guidance.next_action)}
                className="w-full py-3 px-4 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${platformInfo.color}, ${platformInfo.color}dd)`,
                  color: 'white'
                }}
                data-testid="next-action-btn"
              >
                {guidance.next_action.label}
                <ChevronRight className="w-4 h-4" />
              </button>
              
              {guidance.next_action.action_type === 'external_link' && (
                <p className="text-xs text-semantic-muted text-center mt-2">
                  S'ouvre dans un nouvel onglet
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}


/**
 * Collapsible Block Component
 */
function CollapsibleBlock({ title, icon, iconColor, expanded, onToggle, children, testId }) {
  return (
    <div 
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: 'hsl(var(--color-border))' }}
      data-testid={testId}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-semantic-surface-2 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span style={{ color: `hsl(${iconColor})` }}>
            {icon}
          </span>
          <span className="font-medium text-sm text-semantic">{title}</span>
        </div>
        <ChevronDown 
          className={cn(
            "w-4 h-4 text-semantic-muted transition-transform",
            expanded && "rotate-180"
          )}
        />
      </button>
      
      {expanded && (
        <div 
          className="p-3 border-t"
          style={{ borderColor: 'hsl(var(--color-border))' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
